import React, { useState, ChangeEvent } from 'react';
import { getSavedCreditCardsIcon } from '../saved-credit-cards-provider';
import { ICreditCard } from '../../../interfaces';
import { newCreditCardActions, savedCreditCardActions, useAppDispatch, useAppSelector } from '../../../data/store';
import { removeSavedCreditCard } from '../../../data/data-service';
import { UPDATE_SAVED_CREDIT_CARDS_EVENT } from './saved-credit-cards';
import Preloader from '../../../../../../ui/svg/common/preloader';
import InfoIcon from '../../../../../../ui/svg/common/info';
import { updateControl } from '../../../../../../domain/validation/validation';

const selectedStyles = {
    backgroundColor: '#ecf5ff',
    border: 'solid 0.7px #3452ff',
};

interface ISavedCreditCardProps {
    savedCard: ICreditCard;
}

const SavedCreditCard = (props: ISavedCreditCardProps) => {

    const { savedCard } = props;

    const dispatch = useAppDispatch();
    const savedCreditCardToken = useAppSelector(store => store.savedCreditCard.savedCreditCardToken);
    const cvvControl = useAppSelector(store => store.savedCreditCard.cvvControl);

    const [ loading, setLoading ] = useState(false);
    const isSelected = savedCreditCardToken === savedCard.token;

    const selectSavedCard = () => {

        if(savedCreditCardToken !== savedCard.token){
            dispatch(
                savedCreditCardActions.setControl({
                    controlName: 'cvvControl',
                    control: updateControl(cvvControl, '', false)
                })
            );
        }

        dispatch(
            savedCreditCardActions.main({
                savedCreditCardToken: savedCard.token,
                networkTransactionID: savedCard.networkTransactionID || '',
                isRecurringNetworkTransactionID: savedCard.isRecurring,
            })
        );
    };

    const showPopup = () => {
        dispatch(
            savedCreditCardActions.main({
                inUsePopupShown: true,
            })
        )
    };

    const removeSavedCreditCardHandler = async () => {
        if(loading) return;

        setLoading(true);
        const response = await removeSavedCreditCard(savedCard.token);
        setLoading(false);

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData) return;

        const relatedData = response.data.relatedData;
        if(relatedData.isSavedCreditCardInUse){
            showPopup();
            return;
        }

        if(relatedData.savedCreditCards){
            document.dispatchEvent(new CustomEvent(UPDATE_SAVED_CREDIT_CARDS_EVENT, {
                detail: relatedData.savedCreditCards,
            }));
        }
    };

    const cvvInfoPopupInfoClicked = () => {
        dispatch(
            newCreditCardActions.main({
                cvvInfoPopupShown: true,
            })
        );
    };

    const cvvChanged = (evt: ChangeEvent<HTMLInputElement>) => {
        const $target = evt.target as HTMLInputElement;

        dispatch(
            savedCreditCardActions.setControl({
                controlName: 'cvvControl',
                control: updateControl(cvvControl, $target.value, true)
            })
        );
    };

    return (
        <div key={ savedCard.token }
             className="border rounded bg-gray-100 flex px-2 py-3 mb-3 cursor-pointer"
             data-qa="cart-step3-saved-card-box"
             style={ isSelected ? selectedStyles : undefined }
             onClick={ selectSavedCard }>

            <div className="w-4/12 flex flex-col items-center justify-center text-center">
                <div>{ getSavedCreditCardsIcon(savedCard.paymentMethod, isSelected) }</div>

                {
                    !loading &&
                    <button
                        type="button"
                        className="pointer text-tiny text-blue-200 font-bold mt-2 outline-none"
                        onClick={ removeSavedCreditCardHandler }>Remove</button>
                }

                { loading && <Preloader width="15px" height="15px" color="#4f4f4f" classes="mt-2" /> }
            </div>

            <div className="w-8/12 text-gray-500 pl-4 border-l">
                <div>XXXX-XXXX-XXXX-{ savedCard.last4Digits }</div>
                <div>{ savedCard.holderName }</div>
                <div>{ savedCard.expirationDate }</div>

                {
                    isSelected && !savedCard.networkTransactionID &&
                    <div className="flex items-center">
                        <input
                            id="cvv-tb"
                            value={ cvvControl.value }
                            onInput={ cvvChanged }
                            type="text"
                            name="cvv"
                            placeholder="Enter CVV"
                            maxLength={ 4 }
                            title="Enter CVV"
                            size={ 4 }
                            className={ `border text-sm px-1 mr-1 rounded outline-none ${ cvvControl.isValid ? '' : 'border-red' }` }
                            style={{
                                width: '72px',
                            }}
                        />
                        <button
                            type="button"
                            title="What is CVV?"
                            className="text-sm text-gray-500 flex items-center"
                            onClick={ cvvInfoPopupInfoClicked }>
                            <InfoIcon width="15px" color="#777" />
                        </button>
                    </div>
                }
            </div>
        </div>
    )
};

export default SavedCreditCard;